import {
  foldPlaylistPages,
  nextPlaylistPage,
  playlistListingComplete,
  type Playlist,
  type PlaylistEntry,
} from '@visious/core';
import type { AuthApiProblem } from '../api/auth';
import { fetchAuthPlaylistPage } from '../api/playlists';
import type { Platform } from '../platform/instance';
import { fmt, text } from '../strings';

/**
 * Watch later, the reasoning half (2026-08-02). Its own file for `addModel.ts`'s reason: the
 * button and the picker are `.tsx` and must not import `@visious/core` (R13).
 *
 * The one decision that shapes everything here: **membership is read from the appointed playlist
 * itself, fetched whole.** The owned-playlists list embeds only the first 100 entries, and a `POST`
 * appends — so the videos most recently saved are exactly the ones past any truncated window, and a
 * button that read the window would show "add" for the video it added yesterday. Whole costs pages
 * (stride 50, window ~100 — API reference §8), which is why the list is cached per grant and
 * patched in place after every write rather than refetched.
 */

/** Pages are ~100 rows; forty of them is a 2 000-entry watch-later list — past that, stop honestly. */
const MAX_PAGES = 40;

export type WatchLaterListResult =
  | {
      readonly ok: true;
      readonly entries: readonly PlaylistEntry[];
      /** The playlist's current title, so the stored snapshot can be corrected on every read. */
      readonly title: string;
      /** True when {@link MAX_PAGES} stopped the fold — membership past the cap reads as absent. */
      readonly truncated: boolean;
    }
  | { readonly ok: false; readonly problem: AuthApiProblem };

/**
 * Every entry of the appointed playlist, paged until the fold proves itself complete — the same two
 * stop rules the playlist page uses (`playlistListingComplete`, then "no new index").
 */
export async function loadWatchLaterEntries(
  platform: Platform,
  playlistId: string,
): Promise<WatchLaterListResult> {
  const pages: Playlist[] = [];
  for (let fetched = 0; fetched < MAX_PAGES; fetched += 1) {
    const page = nextPlaylistPage(pages);
    if (page === null) break;
    // `null` scopes, `useOwnedPlaylists`'s reasoning: on a read the instance is the authority.
    const result = await fetchAuthPlaylistPage(platform, playlistId, page, null);
    if (!result.ok) return { ok: false, problem: result.error };
    pages.push(result.value);
    if (playlistListingComplete(foldPlaylistPages(pages), result.value.videoCount)) break;
  }

  const listing = foldPlaylistPages(pages);
  return {
    ok: true,
    entries: listing.entries,
    title: pages[0]?.title ?? '',
    truncated: listing.nextPage !== null,
  };
}

/**
 * Where a video sits in the list, as the fact a remove needs: the `indexId`. The first occurrence —
 * a duplicate pair survives one removal with the button still saying "in", which is honest.
 */
export function watchLaterIndexId(
  entries: readonly PlaylistEntry[],
  videoId: string,
): string | null {
  for (const entry of entries) {
    if (entry.video.videoId === videoId && entry.indexId !== null) return entry.indexId;
  }
  return null;
}

/** One choice in the picker. The rows are `addTargetRows`' shape, minus the write states. */
export interface WatchLaterPickerRow {
  readonly playlistId: string;
  readonly title: string;
  readonly countText: string | null;
  /** The already-appointed list — marked, and still pressable (re-picking refreshes the title). */
  readonly current: boolean;
}

export function watchLaterPickerRows(
  playlists: readonly Playlist[] | null,
  currentId: string | null,
): readonly WatchLaterPickerRow[] | null {
  if (playlists === null) return null;
  return playlists.map((playlist) => {
    const title = playlist.title.trim();
    return {
      playlistId: playlist.playlistId,
      title: title === '' ? text().playlists.untitled : playlist.title,
      // `videoCount` rather than `entries.length` — the embedded array caps at 100 (`addModel.ts`).
      countText: fmt().countedNoun(playlist.videoCount, text().playlists.video),
      current: playlist.playlistId === currentId,
    };
  });
}
